import { TeApiContext, useSelector } from '@ecommerce/nest-ssr/common';
import OfferInterface from 'interfaces/offer.interface';
import React, { useContext } from 'react';
import { offersSort } from 'helpers/offers-sort';
import { OffersState, SortType } from '../../../../../reducers/offers';
import ProductSectionBox from '../product-section-box/product-section-box';
import ProductOfferWrapper from './product-offer-wrapper';
import getCapitalizeString from 'helpers/get-capitalize-string';
import offersPrepare from 'helpers/offers-prepare';
import { ProductInterface } from 'modules/product/interfaces/product.interface';

import './product-offers.scss';

const sortTitle = {
    [SortType.BestOffer]: 'najlepsze oferty wg Skąpiec.pl',
    [SortType.PriceAsc]: 'oferty od najtańszej',
    [SortType.PriceDesc]: 'oferty od najdroższej',
    [SortType.ShopScore]: 'oferty od najwyższej oceny'
};

const MIN_DEFAULT_NUMBER_OF_BEST_OFFERS = 4;
const MAX_DEFAULT_NUMBER_OF_BEST_OFFERS = 1;

const OffersByScoreStrategy: React.FC<{ offers: OfferInterface[]; productName: string; activeSort: SortType; isMobile: boolean; }>
    = ({ offers, productName, activeSort, isMobile }) => {
        let bestOffers: OfferInterface[];
        let otherOffers: OfferInterface[];
        const bestOffersWithinLimit: OfferInterface[] = [];

        if (offers.length > MAX_DEFAULT_NUMBER_OF_BEST_OFFERS) {
            bestOffers = offers.slice(0, MAX_DEFAULT_NUMBER_OF_BEST_OFFERS);
            otherOffers = offers.slice(MAX_DEFAULT_NUMBER_OF_BEST_OFFERS);
        } else {
            bestOffers = offers.slice(0, MIN_DEFAULT_NUMBER_OF_BEST_OFFERS);
            otherOffers = offers.slice(MIN_DEFAULT_NUMBER_OF_BEST_OFFERS);
        }

        for (const offer of bestOffers) {
            if (offer.dealer.outOfLimit) {
                otherOffers.unshift(offer);
            } else {
                bestOffersWithinLimit.push(offer);
            }
        }

        const headerTextBest = isMobile ? getCapitalizeString(sortTitle[activeSort]) : ` ${productName} - ${sortTitle[activeSort]}`;
        const headerTextOther = otherOffers.length > 1 ? 'od najtańszej' : 'wg Skąpiec.pl';

        return (
            <>
                {bestOffersWithinLimit.length > 0 && <ProductSectionBox
                    headerText={headerTextBest}
                    padding={isMobile ? 'sm' : 'lg'}
                    tooltipInfo={activeSort === SortType.BestOffer
                        ? {
                            title: 'Jak wybieramy najlepsze oferty?',
                            // eslint-disable-next-line max-len
                            description: ' Ranking oferty jest obliczany na podstawie m.in. ceny na tle konkurencji, warunków i kosztów dostawy oraz opinii o sklepie.'
                        }
                        : null}
                >
                    {bestOffersWithinLimit.map(offer => <ProductOfferWrapper key={offer.id} offer={offer} />)}
                </ProductSectionBox>}

                {otherOffers.length > 0 && <ProductSectionBox headerText={`Pozostałe oferty ${headerTextOther}`} padding={isMobile ? 'sm' : 'lg'}>
                    {otherOffers
                        .sort(offersSort(SortType.PriceAsc)).sort((prev, next) => Number(prev.dealer.outOfLimit) - Number(next.dealer.outOfLimit))
                        .map(offer => <ProductOfferWrapper key={offer.id} offer={offer} />)}
                </ProductSectionBox>}
            </>
        );
    };

const OffersByPriceStrategy: React.FC<{ offers: OfferInterface[]; productName: string; activeSort: SortType; isMobile: boolean; }>
    = ({ offers, productName, activeSort, isMobile }) => {
        const headerText = isMobile ? getCapitalizeString(sortTitle[activeSort]) : ` ${productName} - ${sortTitle[activeSort]}`;

        return (
            <>
                <ProductSectionBox headerText={headerText} padding={isMobile ? 'sm' : 'lg'}>
                    {offers.length > 0 && offers.map(offer => <ProductOfferWrapper key={offer.id} offer={offer} />)}
                </ProductSectionBox>
            </>
        );
    };

const ProductOffers: React.FC<{ productName: string; }> = ({ productName }) => {
    const offers = useSelector<{ product: ProductInterface; offers: OffersState; }, OfferInterface[]>(
        state => state.offers.prepared?.length ? [...state.offers.prepared] : [...offersPrepare(state.product.offers.items)]
    );
    const activeSort = useSelector<{ offers: OffersState; }, SortType>(state => state.offers.activeSort);
    const api = useContext(TeApiContext);

    if (api === null) {
        throw new Error('Invalid API context');
    }

    const isMobile = api.getDevice() === 'mobile';

    return (
        <div className="product-offers-container">
            {activeSort === SortType.BestOffer && <OffersByScoreStrategy offers={[...offers]} productName={productName} activeSort={activeSort}
                isMobile={isMobile}
            />}
            {activeSort !== SortType.BestOffer && <OffersByPriceStrategy offers={offers} productName={productName} activeSort={activeSort}
                isMobile={isMobile}
            />}
        </div>
    );
};

export default ProductOffers;
